import React, { useState, useEffect } from 'react';
import './HTSCodeModal.css';

function HTSCodeModal({ isOpen, onClose, onSelect, selectedCode }) {
  const [htsCodes, setHtsCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (isOpen) {
      fetchHTSCodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, searchTerm]);

  const fetchHTSCodes = async () => {
    try {
      setLoading(true);
      const url = searchTerm
        ? `hts-codes/search?q=${encodeURIComponent(searchTerm)}&limit=1000`
        : `hts-codes?limit=1000`;

      const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/${url}`;
      console.log('Fetching HTS codes from:', apiUrl);

      const response = await fetch(apiUrl, {
        credentials: 'include',
      });

      console.log('Response status:', response.status);

      const data = await response.json();
      console.log('Response data:', data);

      if (data.success) {
        const codes = searchTerm ? data.results : data.codes;
        console.log('Setting codes:', codes?.length || 0);
        setHtsCodes(codes || []);
        setTotalPages(Math.ceil((codes?.length || 0) / itemsPerPage));
      } else {
        console.error('API returned success: false');
        setHtsCodes([]);
      }
    } catch (error) {
      console.error('Error fetching HTS codes:', error);
      setHtsCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  const handleSelect = (code) => {
    onSelect(code);
    onClose();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Paginate results
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCodes = htsCodes.slice(startIndex, endIndex);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 7;

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...');
      }

      // Show pages around current page
      for (
        let i = Math.max(2, currentPage - 1);
        i <= Math.min(totalPages - 1, currentPage + 1);
        i++
      ) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push('...');
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  if (!isOpen) return null;

  return (
    <div className="hts-modal-overlay" onClick={onClose}>
      <div className="hts-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="hts-modal-header">
          <h2>Select HTS Code</h2>
          <button className="hts-modal-close" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="hts-modal-search">
          <input
            type="text"
            placeholder="Search by code or description..."
            value={searchTerm}
            onChange={handleSearch}
            autoFocus
          />
          <div className="hts-search-info">
            {loading ? 'Searching...' : `Found ${htsCodes.length} codes`}
          </div>
        </div>

        <div className="hts-modal-body">
          {loading ? (
            <div className="hts-loading">Loading HTS codes...</div>
          ) : paginatedCodes.length === 0 ? (
            <div className="hts-no-results">
              {searchTerm ? 'No codes found matching your search' : 'No HTS codes available'}
            </div>
          ) : (
            <table className="hts-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCodes.map((hts) => (
                  <tr key={hts.code} className={selectedCode === hts.code ? 'selected' : ''}>
                    <td className="hts-code">{hts.code}</td>
                    <td className="hts-description">{hts.description}</td>
                    <td>
                      <button className="hts-select-btn" onClick={() => handleSelect(hts)}>
                        Select
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="hts-modal-footer">
          <div className="hts-pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="hts-page-btn"
            >
              &lt;
            </button>

            {getPageNumbers().map((page, index) =>
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="hts-page-ellipsis">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`hts-page-btn ${currentPage === page ? 'active' : ''}`}
                >
                  {page}
                </button>
              )
            )}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="hts-page-btn"
            >
              &gt;
            </button>
          </div>

          <div className="hts-page-info">
            Page {currentPage} of {totalPages} ({htsCodes.length} total codes)
          </div>
        </div>
      </div>
    </div>
  );
}

export default HTSCodeModal;
